import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Typography, Segmented, Table, Spin, message, DatePicker, Space, Dropdown, Button, Tag } from 'antd';
import { CalendarOutlined, DownOutlined, DollarOutlined } from '@ant-design/icons';
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { analyticsApi } from 'api/analytics';
import styled from 'styled-components';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const KpiCard = styled(Card)`
  border-radius: 8px;
  .ant-statistic-title { font-size: 13px; color: #8c8c8c; }
`;

const ChartCard = styled(Card)`
  border-radius: 8px;
  margin-bottom: 16px;
  .ant-card-head-title { font-size: 15px; font-weight: 600; }
`;

const DateRangeBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  flex-wrap: wrap;
`;

const PresetButton = styled(Button)<{ $active?: boolean }>`
  ${props => props.$active && `
    background: #1a7a4f;
    color: white;
    border-color: #1a7a4f;
    &:hover {
      background: #15633f !important;
      color: white !important;
      border-color: #15633f !important;
    }
  `}
`;

type DatePreset = 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'this_month' | 'last_month' | 'this_year' | 'last_year' | 'all_time' | 'custom';

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_year', label: 'This year' },
  { key: 'last_year', label: 'Last year' },
  { key: 'all_time', label: 'All time' },
];

function getPresetDates(preset: DatePreset): [Date, Date] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);

  switch (preset) {
    case 'today':
      return [today, tomorrow];
    case 'yesterday': {
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      return [yesterday, today];
    }
    case '7d':
      return [new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), tomorrow];
    case '30d':
      return [new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), tomorrow];
    case '90d':
      return [new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000), tomorrow];
    case 'this_month':
      return [new Date(now.getFullYear(), now.getMonth(), 1), tomorrow];
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 1);
      return [start, end];
    }
    case 'this_year':
      return [new Date(now.getFullYear(), 0, 1), tomorrow];
    case 'last_year':
      return [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear(), 0, 1)];
    case 'all_time':
      return [new Date(2020, 0, 1), tomorrow];
    default:
      return [new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), tomorrow];
  }
}

function formatDateLabel(start: Date, end: Date): string {
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}

const formatCurrency = (value: number) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const Revenue: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any>(null);
  const [historyData, setHistoryData] = useState<any>({ revenue_timeline: [], failed_timeline: [], new_signups: [] });
  const [historyPeriod, setHistoryPeriod] = useState<string>('month');
  const [activePreset, setActivePreset] = useState<DatePreset>('all_time');
  const [dateRange, setDateRange] = useState<[Date, Date]>(getPresetDates('all_time'));

  const fetchData = useCallback(async (start: Date, end: Date, period: string) => {
    try {
      setLoading(true);
      const startStr = start.toISOString();
      const endStr = end.toISOString();
      const [overviewRes, historyRes] = await Promise.all([
        analyticsApi.getRevenueOverview(startStr, endStr),
        analyticsApi.getRevenueHistory(startStr, endStr, period),
      ]);
      setOverview(overviewRes.data);
      setHistoryData(historyRes.data);
    } catch (err) {
      message.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchData(dateRange[0], dateRange[1], historyPeriod);
  }, [dateRange, historyPeriod, fetchData]); // eslint-disable-line

  const handlePreset = (preset: DatePreset) => {
    setActivePreset(preset);
    setDateRange(getPresetDates(preset));
    const [start, end] = getPresetDates(preset);
    const days = (end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000);
    if (days <= 7) setHistoryPeriod('day');
    else if (days <= 90) setHistoryPeriod('day');
    else if (days <= 365) setHistoryPeriod('month');
    else setHistoryPeriod('month');
  };

  const handleCustomRange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setActivePreset('custom');
      setDateRange([dates[0].toDate(), dates[1].toDate()]);
    }
  };

  // Revenue timeline for chart
  const revenueChartData = (historyData.revenue_timeline || []).map((r: any) => ({
    date: r.date,
    revenue: r.revenue,
    enterprise: r.enterprise || 0,
    professional: r.professional || 0,
    other: r.other || 0,
    charges: r.charges,
  }));

  // Failed charges for chart
  const failedChartData = (historyData.failed_timeline || []).map((f: any) => ({
    date: f.date,
    failed_amount: f.amount,
    failed_count: f.count,
  }));

  // Signups for chart
  const signupChartData = (historyData.new_signups || []).map((s: any) => ({
    date: s._id,
    signups: s.count,
  }));

  // Revenue by plan breakdown in range
  const revenueByPlan = overview?.revenue_by_plan_in_range || {};
  const planRevenueData = Object.entries(revenueByPlan).map(([plan, amount]) => ({
    plan: plan.charAt(0).toUpperCase() + plan.slice(1),
    revenue: amount as number,
  }));

  const planColumns = [
    { title: 'Plan', dataIndex: 'plan', key: 'plan', render: (p: string) => {
      const note = overview?.plan_breakdown?.find((b: any) => b.plan === p.toLowerCase())?.note;
      return <span>{p} {note ? <Tag color="orange" style={{ fontSize: 10 }}>{note}</Tag> : null}</span>;
    }},
    { title: 'Active Subs', dataIndex: 'users', key: 'users' },
    { title: 'MRR', dataIndex: 'mrr', key: 'mrr', render: (v: number) => `$${(v || 0).toFixed(0)}` },
  ];

  const morePresets = DATE_PRESETS.slice(5).map(p => ({
    key: p.key,
    label: <span style={{ fontWeight: activePreset === p.key ? 600 : 400 }}>{p.label}</span>,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: 'white', padding: '10px 14px', border: '1px solid #e8e8e8', borderRadius: 6 }}>
          <p style={{ margin: 0, fontWeight: 600, marginBottom: 4 }}>{label}</p>
          {payload.map((entry: any, i: number) => (
            <p key={i} style={{ margin: 0, color: entry.color, fontSize: 13 }}>
              {entry.name}: ${entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <Title level={2}><DollarOutlined /> Revenue Analytics</Title>

      {/* Date Range Bar */}
      <DateRangeBar>
        <CalendarOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />
        <Space size={4} wrap>
          {DATE_PRESETS.slice(0, 5).map(p => (
            <PresetButton
              key={p.key}
              size="small"
              $active={activePreset === p.key}
              onClick={() => handlePreset(p.key)}
            >
              {p.label}
            </PresetButton>
          ))}
          <Dropdown
            menu={{
              items: morePresets,
              onClick: ({ key }) => handlePreset(key as DatePreset),
            }}
          >
            <Button size="small">
              {DATE_PRESETS.slice(5).find(p => p.key === activePreset)?.label || 'More'} <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
        <RangePicker
          size="small"
          onChange={handleCustomRange}
          style={{ marginLeft: 'auto' }}
        />
        <Tag color="blue" style={{ margin: 0 }}>
          {formatDateLabel(dateRange[0], dateRange[1])}
        </Tag>
      </DateRangeBar>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spin size="large" /></div>
      ) : (
        <>
          {/* Top KPIs - Real Stripe Data */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={8} lg={4}>
              <KpiCard>
                <Statistic title="Revenue (in range)" value={overview?.revenue_in_range || 0} prefix="$" precision={0}
                  valueStyle={{ color: '#1a7a4f', fontSize: 28 }} />
              </KpiCard>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <KpiCard>
                <Statistic title="Lifetime Revenue" value={overview?.total_lifetime_revenue || 0} prefix="$" precision={0}
                  valueStyle={{ color: '#1890ff' }} />
              </KpiCard>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <KpiCard>
                <Statistic title="Current MRR" value={overview?.mrr || 0} prefix="$" precision={0}
                  valueStyle={{ color: overview?.mrr > 0 ? '#1a7a4f' : '#ff4d4f' }} />
              </KpiCard>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <KpiCard>
                <Statistic title="Active Subs" value={overview?.active_subscriptions || 0}
                  valueStyle={{ color: overview?.active_subscriptions > 0 ? '#1a7a4f' : '#ff4d4f' }} />
              </KpiCard>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <KpiCard>
                <Statistic title="Charges (in range)" value={overview?.charges_in_range || 0}
                  suffix={overview?.failed_charges_in_range > 0 ? <span style={{ fontSize: 13, color: '#ff4d4f' }}> / {overview.failed_charges_in_range} failed</span> : undefined} />
              </KpiCard>
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <KpiCard>
                <Statistic title="Conversion" value={overview?.conversion_rate || 0} suffix="%" precision={1} />
              </KpiCard>
            </Col>
          </Row>

          {/* Secondary KPIs */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={8} lg={6}>
              <KpiCard>
                <Statistic title="New Users (in range)" value={overview?.new_users_in_range || 0}
                  valueStyle={{ color: '#1890ff' }} />
              </KpiCard>
            </Col>
            <Col xs={8} lg={6}>
              <KpiCard>
                <Statistic title="Ever Paid (all time)" value={overview?.total_ever_paid || 0}
                  valueStyle={{ color: '#722ed1' }} />
              </KpiCard>
            </Col>
            <Col xs={8} lg={6}>
              <KpiCard>
                <Statistic title="One-time Purchases" value={overview?.one_time_purchases || 0} />
              </KpiCard>
            </Col>
            <Col xs={8} lg={6}>
              <KpiCard>
                <Statistic title="Total Users" value={overview?.total_users || 0} />
              </KpiCard>
            </Col>
          </Row>

          {/* Revenue Chart + Plan Breakdown */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={16}>
              <ChartCard
                title="Revenue Over Time (from Stripe)"
                extra={
                  <Segmented
                    options={[
                      { label: 'Daily', value: 'day' },
                      { label: 'Weekly', value: 'week' },
                      { label: 'Monthly', value: 'month' },
                    ]}
                    value={historyPeriod}
                    onChange={(val) => setHistoryPeriod(val as string)}
                    size="small"
                  />
                }
              >
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={revenueChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${v}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="enterprise" stackId="revenue" fill="#1890ff" name="Enterprise ($59/mo)" />
                    <Bar dataKey="professional" stackId="revenue" fill="#1a7a4f" name="Professional ($19/mo)" />
                    <Bar dataKey="other" stackId="revenue" fill="#faad14" name="Other/One-time" />
                  </BarChart>
                </ResponsiveContainer>
                {historyData.totals && (
                  <div style={{ marginTop: 12, padding: '8px 12px', background: '#f6ffed', borderRadius: 6, fontSize: 13 }}>
                    Period total: <strong>{formatCurrency(historyData.totals.revenue)}</strong> from {historyData.totals.successful_charges} successful charges
                    {historyData.totals.failed_charges > 0 && (
                      <span style={{ color: '#ff4d4f' }}> ({historyData.totals.failed_charges} failed)</span>
                    )}
                  </div>
                )}
              </ChartCard>
            </Col>
            <Col xs={24} lg={8}>
              <ChartCard title="Subscriptions">
                <Table
                  dataSource={overview?.plan_breakdown || []}
                  columns={planColumns}
                  rowKey="plan"
                  size="small"
                  pagination={false}
                />
                {planRevenueData.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Title level={5} style={{ marginBottom: 8 }}>Revenue by Plan (in range)</Title>
                    {planRevenueData.map(p => (
                      <div key={p.plan} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <span>{p.plan}</span>
                        <strong>${p.revenue.toLocaleString()}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </ChartCard>
            </Col>
          </Row>

          {/* New Signups + Failed Charges */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <ChartCard title="New Signups (All Users)">
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={signupChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="signups" stroke="#1890ff" fill="#e6f4ff" name="New Signups" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            </Col>
            <Col xs={24} lg={12}>
              <ChartCard title="Failed Charges">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={failedChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${v}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="failed_amount" fill="#ff4d4f" name="Failed Amount" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Revenue;
